import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Subject } from 'rxjs';
import { catchError, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { OverviewDataSource } from '../../../overview/overview-data-source';
import { OverviewDataService, QuoteItem } from '../../../overview/overview.data.service';
import { PermissionService } from '../../services/permission.service';
import { SelectedCustomerService } from '../../services/selected-customer.service';
import { ImageDialogComponent, ImageDialogData } from '../image-dialog/image-dialog.component';
import { openQuoteCreationDialog } from '../quote-creation-dialog/quote-creation-dialog.component';
import { openQuoteDeleteDialog } from '../quote-delete-dialog/quote-delete-dialog.component';
import { OverlayDialogHelper } from '../utils/overlay-dialog.helper';
import {
    PriceSummaryDialogComponent,
    PriceSummaryDialogData,
} from './price-summary-dialog/price-summary-dialog.component';
import { AppService } from '../../services/app.service';
import { openQuoteConversionDialog } from '../quote-conversion-dialog/quote-conversion-dialog.component';
import { QuoteConversion, QuoteService } from '../../services/quote.service';
import { SnackBarService } from '../../services/snack-bar.service';

const CANCEL_DEFAULT_REASON = '19';

@Component({
    selector: 'app-overview-table',
    templateUrl: './overview-table.component.html',
    styleUrls: ['./overview-table.component.scss'],
})
export class OverviewTableComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe$: Subject<void> = new Subject<void>();
    private helper: OverlayDialogHelper<PriceSummaryDialogComponent, PriceSummaryDialogData>;
    private viewer: OverlayDialogHelper<ImageDialogComponent, ImageDialogData>;
    @ViewChild(MatSort, { static: true })
    public sort: MatSort;

    @ViewChild(MatPaginator, { static: true })
    public paginator: MatPaginator;

    @Input()
    public dataSource: OverviewDataSource<any, any>;

    @Output()
    public viewOffer = new EventEmitter<any>();
    @Output()
    public copyOffer = new EventEmitter<any>();
    @Output()
    public deleteOffer = new EventEmitter<any>();
    @Output()
    public pagingError = new EventEmitter<any>();
    @Output()
    public sortError = new EventEmitter<any>();

    @Input()
    public isEmployee: boolean;
    @Input()
    public isAdmin: boolean;

    @Output() public reloadTemplates = new EventEmitter();
    @Output() public reloadUnitTests = new EventEmitter();

    public columnsToDisplayForCustomer = [
        'documentId',
        'projectId',
        'createdDate',
        'schemePreview',
        'title',
        'totalGross',
        'creatorFullName',
        'status',
        'actions',
    ];
    public columnsToDisplayForEmployee = [
        'documentId',
        'projectId',
        'partnerNumber',
        'createdDate',
        'schemePreview',
        'title',
        'totalGross',
        'creator',
        'status',
        'actions',
    ];
    public columnsToDisplay;

    constructor(
        private translationService: TranslateService,
        private permissionService: PermissionService,
        private selectedCustomerService: SelectedCustomerService,
        private dialog: MatDialog,
        private appService: AppService,
        private dataService: OverviewDataService,
        private quoteService: QuoteService,
        private snackBarService: SnackBarService
    ) {
        this.helper = new OverlayDialogHelper<PriceSummaryDialogComponent, PriceSummaryDialogData>(
            document,
            this.dialog,
            PriceSummaryDialogComponent,
            'price-summary-dialog'
        );
        this.viewer = new OverlayDialogHelper<ImageDialogComponent, ImageDialogData>(
            document,
            dialog,
            ImageDialogComponent,
            'image-dialog'
        );
    }

    public ngOnInit() {
        this.setColumnsToDisplay();
        this.dataSource.init(this.paginator, this.sort);
        this.translatePaginator();
        this.translationService.onLangChange.subscribe(() => this.translatePaginator());
        this.selectedCustomerService.customerForPartnerMode$.subscribe(() => this.setColumnsToDisplay());
    }

    private setColumnsToDisplay() {
        this.columnsToDisplay =
            this.permissionService.isUserAnEmployee && !this.isPartnerMode
                ? this.columnsToDisplayForEmployee
                : this.columnsToDisplayForCustomer;
    }

    public get isPartnerMode() {
        return !!this.selectedCustomerService.customerForPartnerMode$?.value;
    }

    private translatePaginator() {
        const intl = this.paginator._intl;
        intl.itemsPerPageLabel = this.translationService.instant('OVERVIEW.TABLE.PAGINATOR.LABEL.ITEMS_PER_PAGE');
        intl.getRangeLabel = (page: number, pageSize: number, length: number) => {
            const start = page * pageSize;
            const end = start < length ? Math.min(start + pageSize, length) : start + pageSize;
            return this.translationService.instant('OVERVIEW.TABLE.PAGINATOR.LABEL.RANGE', {
                page: start + 1,
                pageSize: end,
                length,
            });
        };
        intl.changes.next();
    }

    public ngAfterViewInit(): void {
        // listen to paging events
        this.paginator.page
            .pipe(
                mergeMap(() => this.dataSource.loadOverviewData()),
                catchError(() => {
                    this.pagingError.emit();
                    return EMPTY;
                })
            )
            .subscribe();

        // listen to sort events
        this.sort.sortChange
            .pipe(
                tap(() => this.dataSource.resetPaging()), // reset to first page when sorting
                mergeMap(() => this.dataSource.loadOverviewData()),
                catchError(() => {
                    this.sortError.emit();
                    return EMPTY;
                })
            )
            .subscribe();
    }

    public getCreatorFullName(obj) {
        return obj.authorName ? `${obj.authorName.firstName || ''} ${obj.authorName.familyName || ''}` : '';
    }

    public isNew(obj: QuoteItem) {
        return obj.status.toUpperCase() === 'NEW';
    }

    public getViOfferUrl(obj: QuoteItem) {
        return this.isNew(obj) ? null : this.appService.getViOfferUrl(obj.quoteId);
    }

    public onViewClick(quote) {
        this.viewOffer.emit(quote);
    }

    public handlePriceSummaryEvent(event: MouseEvent, obj) {
        if (obj.totalGross || obj.totalDiscount || obj.totalPrice) {
            this.helper.handleEvent(event, obj);
        }
    }

    public ngOnDestroy() {
        this.helper.destroy();
    }

    public onCopyClick(quote: QuoteItem) {
        openQuoteCreationDialog(
            this.dialog,
            this.translationService.instant('OVERVIEW.DIALOG.COPY_OFFER.TITLE'),
            this.translationService.instant('COMMON.DIALOG.CONFIRM'),
            this.translationService.instant('COMMON.DIALOG.CANCEL'),
            `${this.translationService.instant('OVERVIEW.DIALOG.COPY_OFFER.COPY')} ${quote.customerPurchaseNumber}`
        ).subscribe((customerPurchaseNumber) => {
            if (customerPurchaseNumber) {
                this.copyOffer.emit({
                    id: quote.id,
                    customerPurchaseNumber,
                });
            }
        });
    }

    public onImageHover(event: MouseEvent, obj) {
        if (obj.status.toLowerCase() !== 'new') {
            this.viewer.handleEvent(event, {
                quoteId: obj.id,
                imageUrl: obj.scheme.preview,
            });
        }
    }

    public onDeleteClick(quote) {
        let reasons$: Subject<{ [key: string]: string }>;
        if (this.isEmployee && quote.status.toLowerCase() !== 'new') {
            reasons$ = new Subject<{ [key: string]: string }>();
            this.dataService.getAvailableCancellationReasons().pipe(takeUntil(this.unsubscribe$)).subscribe(reasons$);
        }

        openQuoteDeleteDialog(
            this.dialog,
            this.translationService.instant('OVERVIEW.DIALOG.DELETE_OFFER.TITLE'),
            `${this.translationService.instant('OVERVIEW.DIALOG.DELETE_OFFER.MESSAGE')} ${
                quote.customerPurchaseNumber
            }`,
            this.translationService.instant('COMMON.DIALOG.CONFIRM'),
            this.translationService.instant('COMMON.DIALOG.CANCEL'),
            reasons$
        ).subscribe((reason) => {
            reason = reason === true ? CANCEL_DEFAULT_REASON : reason;
            if (reason) {
                this.deleteOffer.emit({
                    ...quote,
                    reason,
                });
            }
        });
    }

    public onConvert(quote: QuoteItem) {
        openQuoteConversionDialog(this.dialog, quote).subscribe((conversion: QuoteConversion) => {
            this.quoteService.convertQuote(conversion).subscribe(() => {
                this.snackBarService.openSnackBar({
                    message: this.translationService.instant('OVERVIEW.CONVERT.SUCCESS'),
                    isFailure: false,
                });
                if (conversion.type === 'template') {
                    this.reloadTemplates.emit();
                } else {
                    this.reloadUnitTests.emit();
                }
            });
        });
    }
}
